import CalendarTableSettings from '@app/model/company_hotel/settings/CalendarTableSettings';
import CompanyHotelSettings from '@app/model/company_hotel/settings/CompanyHotelSettings';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanyHotelSettingsService } from '@app/model/company_hotel/service/CompanyHotelSettingsService';
import CalendarFiltersSettings from '@app/model/company_hotel/settings/CalendarFiltersSettings';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';
import CompanyHotelGeneralSettingsInput from '@app/model/company_hotel/input/CompanyHotelGeneralSettingsInput';
import CompanyHotelCalendarSettingsInput from '@app/model/company_hotel/input/CompanyHotelCalendarSettingsInput';
import CompanyHotelPriceSettingsInput from '@app/model/company_hotel/input/CompanyHotelPriceSettingsInput';
import CompanyHotelAvailabilitySettingsInput
	from '@app/model/company_hotel/input/CompanyHotelAvailabilitySettingsInput';
import CompanyHotelPriceRecommendationSettings
	from '@app/model/company_hotel/settings/CompanyHotelPriceRecommendationSettings';
import CompanyHotelCalendarExportSettingsInput
	from '@app/model/company_hotel/input/CompanyHotelCalendarExportSettingsInput';


export function useSaveCompanyHotelCalendarTableSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CalendarTableSettings
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCalendarTableSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelCalendarFiltersSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CalendarFiltersSettings
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCalendarFiltersSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelGeneralSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CompanyHotelGeneralSettingsInput
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelGeneralSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelCalendarSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CompanyHotelCalendarSettingsInput
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelCalendarSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
            void queryClient.invalidateQueries({ queryKey: ['market-state'] });
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelPriceSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CompanyHotelPriceSettingsInput
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelPriceSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelAvailabilitySettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CompanyHotelAvailabilitySettingsInput
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelAvailabilitySettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelCalendarExportSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: CompanyHotelCalendarExportSettingsInput
	}) => any,
	onSuccess?: (data: CompanyHotelSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelCalendarExportSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelPriceRecommendationSettingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		input: { enforcePricingArchitectureHierarchy: boolean }
	}) => any,
	onSuccess?: (data: CompanyHotelPriceRecommendationSettings) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelSettingsService.saveCompanyHotelPriceRecommendationSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data, variables) => {
			replaceCompanyHotelPriceRecommendationSettingsInCache(queryClient, variables.companyHotelId, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

const replaceCompanyHotelSettingsInCache = (queryClient: QueryClient, companyHotelId: number, data: CompanyHotelSettings) => {
	queryClient.setQueryData<CompanyHotel>(CompanyHotelQueryKeys.detail(companyHotelId), (old) => {
		return !!old ? Object.assign(old, { settings: data }) : old;
	});
}
;
const replaceCompanyHotelPriceRecommendationSettingsInCache = (queryClient: QueryClient, companyHotelId: number, data: CompanyHotelPriceRecommendationSettings) => {
	queryClient.setQueryData<CompanyHotel>(CompanyHotelQueryKeys.detail(companyHotelId), (old) => {
		return !!old ? Object.assign(old, { priceRecommendationSettings: data }) : old;
	});

};
